import { logger } from "../utils/logger";
// Deployment Checker & Backend Wake-Up Service
// This automatically pings the backend on page load to ensure it's deployed

const BACKEND_URL = `https://www.creabiz.co/api/make-server-e799d0d8`;

interface BackendStatus {
  isOnline: boolean;
  version?: string;
  responseTime?: number;
  error?: string;
}

async function checkEndpoint(
  endpoint: string, 
  description: string
): Promise<{ success: boolean; time: number; data?: any }> {
  const startTime = Date.now();
  
  try {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || publicAnonKey}`,
      },
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });
    
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    if (response.ok) {
      const data = await response.json();
      logger.log(
        `%c✅ ${description}`,
        'color: #00ff00',
        `(${responseTime}ms)`
      );
      return { success: true, time: responseTime, data };
    } else {
      logger.log(
        `%c⚠️  ${description} - Status ${response.status}`,
        'color: #ffa500',
        `(${responseTime}ms)`
      );
      return { success: false, time: responseTime };
    }
  } catch (error: any) {
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    logger.log(
      `%c❌ ${description} - ${error.message}`,
      'color: #ff6b6b',
      `(${responseTime}ms)`
    );
    return { success: false, time: responseTime };
  }
}

export async function checkBackendDeployment(): Promise<BackendStatus> {
  // Check if we've already warned about backend being offline in this session
  if (sessionStorage.getItem('backend-check-complete')) {
    return {
      isOnline: false,
      error: 'Backend check already performed (offline)'
    };
  }

  // Test critical endpoints in sequence
  const healthCheck = await checkEndpoint('/health', 'Health Check');
  
  if (!healthCheck.success) {

    // Mark check as complete to avoid repeated warnings
    sessionStorage.setItem('backend-check-complete', 'true');
    
    return {
      isOnline: false,
      error: 'Backend not responding - likely not deployed'
    };
  }

  // Health check passed - test more endpoints
  const versionCheck = await checkEndpoint('/version', 'Version Info');
  const heroCheck = await checkEndpoint('/homepage/hero', 'Hero Section API');
  const logosCheck = await checkEndpoint('/homepage/logos', 'Logos Section API');
  const whyChooseCheck = await checkEndpoint('/homepage/why-choose', 'Why Choose Section API');

  const allPassed = healthCheck.success && 
                    versionCheck.success && 
                    heroCheck.success && 
                    logosCheck.success && 
                    whyChooseCheck.success;

  if (allPassed) {

    if (versionCheck.data?.version) {

    }
    if (versionCheck.data?.message) {

    }
    logger.log(`%c   → Average Response Time: ${Math.round((healthCheck.time + versionCheck.time) / 2)}ms`, 'color: #00ff00');

  } else {

  }

  return {
    isOnline: allPassed,
    version: versionCheck.data?.version,
    responseTime: Math.round((healthCheck.time + versionCheck.time) / 2)
  };
}

// Auto-run check on import (with slight delay to let page settle)
setTimeout(() => {
  checkBackendDeployment().catch(err => {
    logger.error('Deployment check failed:', err);
  });
}, 500);

// Export for manual checking
(window as any).checkBackend = checkBackendDeployment;
logger.log('%c💡 TIP: Run window.checkBackend() to manually re-check backend status', 'color: #00aaff; font-style: italic');